import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUserModal } from "../contexts/UserModalContext";
import { fetchUsers, impersonateUser, User } from "../Services/APIService";

const UserModal: React.FC = () => {
  const { isOpen, closeUserModal } = useUserModal();
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [impersonating, setImpersonating] = useState(false);
  const isManagerView = localStorage.getItem("isLoginAsManager") === "true";

  useEffect(() => {
    if (!isOpen) return;
    const loadUsers = async () => {
      setLoading(true);
      const data = await fetchUsers();
      setUsers(data);
      setLoading(false);
    };
    loadUsers();
  }, [isOpen]);

  if (!isOpen) return null;

  // ✅ Handle selecting a user to impersonate
  const handleSelect = async (manager: User) => {
    setImpersonating(true);
    const result = await impersonateUser(manager.userId);
    if (!result?.user) {
      alert("Impersonation failed");
      setImpersonating(false);
      return;
    }

    if (!localStorage.getItem("OriginalUserId")) {
      localStorage.setItem("OriginalUserId", localStorage.getItem("UserId") || "");
      localStorage.setItem("OriginalToken", localStorage.getItem("authToken") || "");
      localStorage.setItem("OriginalUserTypeId", localStorage.getItem("userTypeId") || "");
      localStorage.setItem("OriginalUserFullName", localStorage.getItem("userFullName") || "");
    }

    localStorage.setItem("authToken", result.accessToken);
    localStorage.setItem("refreshToken", result.refreshToken);
    localStorage.setItem("UserId", String(result.user.userId));
    localStorage.setItem("userTypeId", String(result.user.userTypeId));
    localStorage.setItem("tenantId", String(result.user.tenantId));
    localStorage.setItem("groupId", String(result.user.groupId));
    localStorage.setItem("userFullName", String(result.user.userName));
    localStorage.setItem("IsImpersonating", "true");

    window.location.replace(`/#/dashboard`);
    window.location.reload();
  };

  // ✅ Handle "Login as Manager" button in badge row
  const handleLoginAsManager = async () => {
    if (isManagerView) {
      localStorage.removeItem("isLoginAsManager");
      localStorage.removeItem("IsImpersonating");
      window.location.replace(`/#/dashboard`);
      window.location.reload();
      return;
    }
    localStorage.setItem("isLoginAsManager", "true");
    localStorage.setItem("IsImpersonating", "true");
    window.location.replace(`/#/dashboard`);
    window.location.reload();
  };

  const filteredUsers = users.filter(
    (user) =>
      user.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={closeUserModal}
        style={{
          position: "fixed",
          inset: 0,
          backgroundColor: "rgba(0,0,0,0.45)",
          backdropFilter: "blur(2px)",
          zIndex: 9999,
        }}
      />

      {/* Modal */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          backgroundColor: "#fff",
          borderRadius: "16px",
          boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
          width: "420px",
          maxWidth: "95vw",
          maxHeight: "85vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          zIndex: 9999,
        }}
      >
        {/* Loading Overlay */}
        {impersonating && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              backgroundColor: "rgba(255,255,255,0.85)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10,
              borderRadius: "16px",
              flexDirection: "column",
              gap: "12px",
            }}
          >
            <div
              style={{
                width: "36px",
                height: "36px",
                border: "4px solid #e2e8f0",
                borderTop: "4px solid #7c3aed",
                borderRadius: "50%",
                animation: "spin 0.8s linear infinite",
              }}
            />
            <span style={{ color: "#374151", fontWeight: 600 }}>
              Switching account...
            </span>
          </div>
        )}

        {/* ✅ Gradient Header */}
        <div
          style={{
            background: "linear-gradient(45deg, #84556bb3 34%, #000080b3)",
            padding: "22px 20px 20px 20px",
            position: "relative",
          }}
        >
          {/* Close Button */}
          <button
            onClick={closeUserModal}
            style={{
              position: "absolute",
              top: "14px",
              right: "14px",
              background: "rgba(255,255,255,0.2)",
              border: "none",
              borderRadius: "50%",
              width: "28px",
              height: "28px",
              color: "#fff",
              fontSize: "16px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              lineHeight: 1,
            }}
          >
            ×
          </button>

          {/* Title */}
          <h2
            style={{
              margin: "0 0 4px 0",
              fontSize: "22px",
              fontWeight: 700,
              color: "#fff",
            }}
          >
            Login as   
          {/* ✅ Login as Manager Button inside header */}
          <button
            onClick={!impersonating ? handleLoginAsManager : undefined}
            disabled={impersonating}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              backgroundColor: "rgba(255,255,255,0.18)",
              border: "1px solid rgba(255,255,255,0.35)",
              borderRadius: "8px",
              padding: "8px 14px",
              color: "#fff",
              fontSize: "13px",
              fontWeight: 600,
              cursor: impersonating ? "not-allowed" : "pointer",
              backdropFilter: "blur(4px)",
              transition: "background 0.2s",
              marginLeft: "8px"
            }}
            onMouseEnter={(e) =>
              !impersonating &&
              ((e.currentTarget as HTMLButtonElement).style.backgroundColor =
                "rgba(255,255,255,0.28)")
            }
            onMouseLeave={(e) =>
              ((e.currentTarget as HTMLButtonElement).style.backgroundColor =
                "rgba(255,255,255,0.18)")
            }
          >
            <span
              style={{
                width: "24px",
                height: "24px",
                borderRadius: "6px",
                background: "rgba(255,255,255,0.25)",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                fontSize: "13px",
                marginLeft: "8px"
              }}
            >
              {isManagerView ? "←" : "M"}
            </span>
            {isManagerView ? "Back to Manager" : " Manager"}
          </button>
          </h2>
          <p style={{ margin: "0 0 16px 0", fontSize: "13px", color: "rgba(255,255,255,0.75)" }}>
            Select a user account to continue
          </p>

        </div>

        {/* Search */}
        <div style={{ padding: "16px 16px 8px 16px" }}>
          <div style={{ position: "relative" }}>
            <span
              style={{
                position: "absolute",
                left: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                fontSize: "14px",
                color: "#9ca3af",
              }}
            >
              🔍
            </span>
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              disabled={impersonating}
              style={{
                width: "100%",
                padding: "10px 12px 10px 36px",
                borderRadius: "10px",
                border: "1px solid #e5e7eb",
                fontSize: "14px",
                outline: "none",
                backgroundColor: "#f9fafb",
                boxSizing: "border-box",
                color: "#111827",
              }}
            />
          </div>
        </div>

        {/* User List */}
        <div style={{ overflowY: "auto", flex: 1, paddingBottom: "8px" }}>
          {loading ? (
            <div
              style={{
                padding: "40px",
                textAlign: "center",
                color: "#6b7280",
                fontSize: "14px",
              }}
            >
              Loading users...
            </div>
          ) : filteredUsers.length === 0 ? (
            <div
              style={{
                padding: "40px 20px",
                textAlign: "center",
                color: "#9ca3af",
              }}
            >
              <div style={{ fontSize: "36px", marginBottom: "8px" }}>👤</div>
              <div style={{ fontSize: "14px", fontWeight: 500 }}>
                {searchTerm ? "No users match your search" : "No users found"}
              </div>
            </div>
          ) : (
            <div>
              {filteredUsers.map((user) => (
                <div
                  key={user.userId}
                  onClick={!impersonating ? () => handleSelect(user) : undefined}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "12px 16px",
                    cursor: impersonating ? "not-allowed" : "pointer",
                    transition: "background 0.15s",
                    borderBottom: "1px solid #f3f4f6",
                  }}
                  onMouseEnter={(e) =>
                    !impersonating &&
                    ((e.currentTarget as HTMLDivElement).style.background = "#f5f3ff")
                  }
                  onMouseLeave={(e) =>
                    ((e.currentTarget as HTMLDivElement).style.background = "transparent")
                  }
                >
                  {/* Avatar */}
                  <div
                    style={{
                      width: "42px",
                      height: "42px",
                      borderRadius: "10px",
                      background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 700,
                      fontSize: "17px",
                      flexShrink: 0,
                    }}
                  >
                    {user.userName.charAt(0).toUpperCase()}
                  </div>

                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: "14px",
                        fontWeight: 600,
                        color: "#111827",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {user.userName}
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#6b7280",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {user.email}
                    </div>
                  </div>

                  {/* Arrow */}
                  <span style={{ color: "#9ca3af", fontSize: "16px" }}>→</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "12px 16px 16px 16px",
            borderTop: "1px solid #f3f4f6",
          }}
        >
          <button
            onClick={closeUserModal}
            disabled={impersonating}
            style={{
              width: "100%",
              padding: "11px",
              borderRadius: "10px",
              border: "1px solid #e5e7eb",
              background: "#f9fafb",
              color: "#374151",
              fontSize: "14px",
              fontWeight: 600,
              cursor: impersonating ? "not-allowed" : "pointer",
            }}
          >
            Cancel
          </button>
        </div>
      </div>

      {/* Spinner keyframe */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

export default UserModal;